import React from 'react';
import NavBar from '../../components/NavBar';
import Footer from '../../components/Footer';
import './Privacy.css';

const Privacy: React.FC = () => {
    return (
        <>
            <NavBar inverted={true} />
            <div className="privacy-page">
                <div className="privacy-container">
                    <div className="privacy-header">
                        <h1>Privacy Policy</h1>
                        <p className="privacy-last-updated">Last Updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                    </div>

                    <div className="privacy-content">
                        <section className="privacy-section">
                            <h2>1. Introduction</h2>
                            <p>
                                Welcome to USublease ("we," "us," "our," or "USublease"). We are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy describes how we collect, use, disclose, and protect your information when you access or use the USublease website, mobile application, and related services (collectively, the "Platform" or "Service").
                            </p>
                            <p>
                                By accessing or using the Platform, you agree to the terms of this Privacy Policy. If you do not agree with our policies and practices, please do not use our Platform.
                            </p>
                        </section>

                        <section className="privacy-section">
                            <h2>2. Information We Collect</h2>
                            <p>
                                We collect several types of information from and about users of our Platform, including:
                            </p>
                            <h3>2.1 Personal Information You Provide</h3>
                            <ul>
                                <li><strong>Account Registration:</strong> When you create an account, we may collect your name, email address, phone number, university or college affiliation, and password.</li>
                                <li><strong>Profile Information:</strong> You may choose to provide additional profile details such as a profile picture, biography, and housing preferences.</li>
                                <li><strong>Listings Data:</strong> If you list a property, we collect information about the listing, including address, photos, pricing, lease terms, and description.</li>
                                <li><strong>Communications:</strong> We collect messages and content you send when communicating with other users through our Platform's messaging system, or when you contact us for support.</li>
                            </ul>

                            <h3>2.2 Information Collected Automatically</h3>
                            <ul>
                                <li><strong>Usage Details:</strong> We collect details of your visits to our Platform, including traffic data, location data, logs, and resources that you access and use.</li>
                                <li><strong>Device Information:</strong> We collect information about your computer, mobile device, and internet connection, including your IP address, operating system, browser type, and unique device identifiers.</li>
                                <li><strong>Cookies and Tracking:</strong> We use cookies, web beacons, and other tracking technologies to gather information about your activities on the Platform over time.</li>
                            </ul>
                        </section>

                        <section className="privacy-section">
                            <h2>3. How We Use Your Information</h2>
                            <p>
                                We use the information we collect to operate, maintain, and improve our Platform, including:
                            </p>
                            <ul>
                                <li>To provide and personalize the services and listings on the Platform;</li>
                                <li>To verify your identity (using student ID or government ID) to maintain a trusted community;</li>
                                <li>To facilitate direct communications between Sublessees and Sublessors;</li>
                                <li>To process and manage payments or promotions;</li>
                                <li>To detect, prevent, and address fraud, security breaches, or other illegal activities;</li>
                                <li>To provide customer support and respond to your inquiries;</li>
                                <li>To monitor and analyze usage trends and enhance user experience;</li>
                                <li>To notify you about changes to our Platform or policies.</li>
                            </ul>
                        </section>

                        <section className="privacy-section">
                            <h2>4. How Your Information is Shared</h2>
                            <p>
                                We do not sell or rent your personal information to third parties. We share your information only in the following circumstances:
                            </p>
                            <ul>
                                <li><strong>With Other Users:</strong> When you create a listing or send messages, certain details of your profile and listing are visible to other users on the Platform.</li>
                                <li><strong>With Service Providers:</strong> We may share information with trusted third-party service providers who assist us in operating our Platform, conducting our business, or serving our users (e.g., hosting providers, database services, and payment processors), subject to confidentiality obligations.</li>
                                <li><strong>For Legal Compliance:</strong> We may disclose your information to comply with any court order, law, or legal process, including to respond to any government or regulatory request.</li>
                                <li><strong>To Protect Rights and Safety:</strong> We may disclose information if we believe it is necessary to protect the rights, property, or safety of USublease, our users, or others.</li>
                                <li><strong>Business Transfers:</strong> In the event of a merger, acquisition, reorganization, or sale of assets, your information may be transferred as part of the transaction.</li>
                            </ul>
                        </section>

                        <section className="privacy-section">
                            <h2>5. Data Security</h2>
                            <p>
                                We implement industry-standard administrative, technical, and physical security measures designed to protect your personal information from unauthorized access, loss, alteration, or disclosure. However, no method of transmission over the internet or method of electronic storage is 100% secure, and we cannot guarantee its absolute security.
                            </p>
                        </section>

                        <section className="privacy-section">
                            <h2>6. Cookies and Third-Party Services</h2>
                            <p>
                                We use cookies and similar technologies to enhance your experience, analyze usage, and customize our service.
                            </p>
                            <p>
                                Our Platform may also integrate third-party services, such as Mapbox for map-based search and Firebase for backend authentication and hosting. These services may collect information in accordance with their own privacy policies. We encourage you to review their terms.
                            </p>
                        </section>

                        <section className="privacy-section">
                            <h2>7. Your Choices and Rights</h2>
                            <p>
                                You have control over your personal information:
                            </p>
                            <ul>
                                <li><strong>Access and Update:</strong> You can access, review, and change your profile information by logging into the Platform and visiting your profile settings page.</li>
                                <li><strong>Listing Management:</strong> You can edit, update, or remove your listings at any time through your dashboard.</li>
                                <li><strong>Account Deletion:</strong> If you wish to delete your account, please contact us at admin@usublease.com. Upon deletion, your active listings and profile information will be removed from public view, although some data may be retained for compliance, legal, or archival purposes.</li>
                            </ul>
                        </section>

                        <section className="privacy-section">
                            <h2>8. Children's Privacy</h2>
                            <p>
                                Our Platform is intended for use by college students and adults. We do not knowingly collect personal information from children under the age of 13. If we learn that we have collected personal information from a child under 13, we will take steps to delete that information as quickly as possible.
                            </p>
                        </section>

                        <section className="privacy-section">
                            <h2>9. Changes to Our Privacy Policy</h2>
                            <p>
                                We reserve the right to modify or update this Privacy Policy at any time. We will notify you of any material changes by posting the new Privacy Policy on this page and updating the "Last Updated" date at the top of the policy. Your continued use of the Platform after such changes constitutes your acceptance of the updated Privacy Policy.
                            </p>
                        </section>

                        <section className="privacy-section">
                            <h2>10. Contact Information</h2>
                            <p>
                                If you have any questions, concerns, or feedback regarding this Privacy Policy or our privacy practices, please contact us at:
                            </p>
                            <p>
                                <strong>USublease</strong><br />
                                Email: <a href="mailto:admin@usublease.com">admin@usublease.com</a><br />
                                Website: <a href="https://usublease.com">www.usublease.com</a>
                            </p>
                        </section>
                    </div>
                </div>
            </div>
            <Footer />
        </>
    );
};

export default Privacy;
